import React, { useEffect, useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import CloudStorage from "../../../../CloudStorage/CloudStorage";
import { urtoast } from "../../../Layouts/CustomPopup/eventEmitter";
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from "react-redux";
import { updateProfile } from "../../../../redux/profile/action";
import ProfilePic from "../../../../assests/imgs/Profileplaceholder.svg";
import Cropper from "react-easy-crop";
import { getCroppedImg } from "../../../../utils/cropUtils";
import ProfileImageWithFallback from "../../../../common/ProfileImageWithFallback";
import { validImageTypes } from "../../../../assests/extention/fileextention";
import { useFileValidator } from "../../../../hooks/useFileValidator";
import UploadLoader from "../../../Common/Loader/UploadLoader";

export default function UploadProfileImage({ formData }) {
  const [selectedFile, setSelectedFile] = useState(formData?.profile_image);
  const [tempImage, setTempImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const { validating, validateFiles } = useFileValidator();

  const dispatch = useDispatch();

  useEffect(() => {
    if (formData?.profile_image) {
      setSelectedFile(formData?.profile_image);
    }
  }, [formData]);

  const handleUpload = async (files) => {
    if (files.length === 0) return;

    // 🛡️ Validate all files via backend API
    const isValid = await validateFiles(files);
    if (!isValid) return;

    const file = files[0];
    if (!file?.type?.startsWith("image/")) {
      urtoast("Only images are allowed", 2);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      urtoast("File size exceeds 10MB limit.", 2);
      return;
    }

    if (validImageTypes.includes(file.type)) {
      const imageURL = URL.createObjectURL(file);
      setTempImage(imageURL);
      setShowCropper(true);
    } else {
      urtoast(`This file type is not supported`, 2);
    }
  };

  const onDrop = (acceptedFiles) => {
    handleUpload(acceptedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
  });

  const onCropComplete = useCallback((croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const croppedFile = await getCroppedImg(tempImage, croppedAreaPixels);
      const uniqueId = uuidv4();
      const storageRef = ref(getStorage(), `/Category/${uniqueId}_${croppedFile.name}`);
      const uploadTask = uploadBytesResumable(storageRef, croppedFile);

      uploadTask.on(
        "state_changed",
        null,
        (error) => {
          urtoast(`Error uploading file: ${error.message}`);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            setSelectedFile(url);
            setShowCropper(false);
            handleSubmit(url);
          } catch (error) {
            urtoast(`Error getting download URL: ${error.message}`);
          }
        }
      );
    } catch (err) {
      console.error(err);
      urtoast("Failed to crop image", 2);
    }
  };

  const handleSubmit = (url) => {
    const data = {
      ...formData,
      type: "profile",
      profile_image: url,
    };
    dispatch(updateProfile(data));
  };

  return (
    <div className="add-portfolio position-relative">
      {validating && <UploadLoader />}
      <div className="row">
        <div className="image-container col-md-3 col-4">
          <ProfileImageWithFallback url={selectedFile} className="cover-img" style={{ width: 150, height: 150, borderRadius: 100 }} />
          <button
            {...getRootProps({ className: "dropzone borderstylex" })}
            type="button"
            className="edit-btn-profile"
            style={{ left: 50 }}
          >
            <input {...getInputProps()} />
            <img
              src={`${CloudStorage.cloudurl}/Assets/Images/common/camera.svg`}
              alt=""
            />
          </button>
        </div>
      </div>

      {/* Cropper Modal */}
      {showCropper && tempImage && (
        <div
          style={{
            position: "relative",
            width: 300,
            height: 300,
            margin: "20px auto",
            background: "#333",
          }}
        >
          <Cropper
            image={tempImage}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
          <button
            type="button"
            onClick={handleCropSave}
            style={{
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%)",
              padding: "5px 15px",
              background: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: 5,
              cursor: "pointer",
            }}
          >
            Crop & Save
          </button>
        </div>
      )}
    </div>
  );
}
